import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getFirestore,
  collection,
  getDocs,
  query,
  orderBy,
} from "firebase/firestore";
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";

const Articles = () => {
  const [articlesList, setArticlesList] = useState([]);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAllArticles = async () => {
      try {
        const db = getFirestore();
        const storage = getStorage();

        const articlesRef = collection(db, "articles");
        const articlesQuery = query(
          articlesRef,
          orderBy("timestamp", "desc")
        );

        const snapshot = await getDocs(articlesQuery);

        if (snapshot.empty) {
          setArticlesList([]);
          return;
        }

        const allArticles = await Promise.all(
          snapshot.docs.map(async (doc) => {
            const data = doc.data();
            let imageUrl = "/images/placeholder.jpg";

            if (data.image_url) {
              try {
                const imageRef = ref(storage, data.image_url);
                imageUrl = await getDownloadURL(imageRef);
              } catch (err) {
                console.warn("Görsel yüklenemedi:", err.message);
              }
            }

            let formattedDate = "Tarih belirtilmemiş";
            if (data.timestamp) {
              try {
                formattedDate = data.timestamp
                  .toDate()
                  .toLocaleDateString("tr-TR", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  });
              } catch {}
            }

            return {
              id: doc.id,
              title: data.title || "Başlıksız Makale",
              content: data.content || "İçerik bulunamadı.",
              date: formattedDate,
              imageUrl,
            };
          })
        );

        setArticlesList(allArticles);
      } catch (err) {
        console.error(err);
        setError("Makaleler yüklenirken bir hata oluştu.");
      }
    };

    fetchAllArticles();
  }, []);

  const handleCardClick = (id) => {
    navigate(`/article/${id}`);
  };

  if (error) {
    return (
      <div className="news-error">
        <p>⚠️ {error}</p>
      </div>
    );
  }

  if (articlesList.length === 0) {
    return (
      <div className="news-empty">
        <p>Henüz makale eklenmemiş.</p>
      </div>
    );
  }

  return (
    <div className="news-section">
      <h2 className="news-header">Tüm Makaleler</h2>

      <div className="news-grid">
        {articlesList.map((item) => (
          <div
            key={item.id}
            className="news-card"
            onClick={() => handleCardClick(item.id)}
          >
            <div className="news-image-container">
              <img
                src={item.imageUrl}
                alt={item.title}
                className="news-image"
                onError={(e) => {
                  e.currentTarget.src = "/images/placeholder.jpg";
                }}
              />
            </div>

            <div className="news-content">
              <h3 className="news-title">{item.title}</h3>

              <div className="news-description">
                <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                  {item.content.length > 130
                    ? item.content.slice(0, 130) + "..."
                    : item.content}
                </ReactMarkdown>
              </div>

              <div className="news-meta">
                <span>Tarih: {item.date}</span>
              </div>

              <button
                className="news-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCardClick(item.id);
                }}
              >
                Makaleyi Oku
              </button>
            </div>
          </div>
        ))}
      </div>

      <style>{`
        .news-section { padding: 70px 20px; background-color: #f9fafc; font-family: "Inter", sans-serif; }
        .news-header { text-align: center; font-size: 2.2rem; font-weight: 700; margin-bottom: 45px; color: #1a1a1a; }
        .news-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 25px; max-width: 1300px; margin: 0 auto; }

        .news-card {
          background-color: #fff;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          transition: all 0.3s ease;
          cursor: pointer;
          display: flex;
          flex-direction: column;
        }

        .news-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 18px rgba(0,0,0,0.15);
        }

        .news-image-container { height: 200px; background: #f5f5f5; }
        .news-image { width: 100%; height: 100%; object-fit: cover; }

        .news-content {
          padding: 18px 20px 24px;
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .news-title {
          font-size: 1.1rem;
          font-weight: 600;
          margin-bottom: 10px;
          color: #333;
        }

        .news-description {
          font-size: 0.9rem;
          color: #666;
          line-height: 1.5;
          margin-bottom: 15px;
          flex-grow: 1;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .news-meta {
          font-size: 0.85rem;
          color: #777;
          margin-bottom: 12px;
        }

        .news-button {
          padding: 12px;
          background-color: #ffc107;
          color: #333;
          border: none;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.3s ease;
        }

        .news-button:hover {
          background-color: #e0a800;
        }

        .news-error, .news-empty {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 80px 20px;
          font-size: 1rem;
          color: #555;
        }
      `}</style>
    </div>
  );
};

export default Articles;
